import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, ChevronDown, Info, ShieldCheck, Download } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

function fmtPct(n) { if (n == null) return '—'; return `${Number(n).toFixed(1)}%`; }
function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-10 px-3 pr-8 text-[12.5px] outline-none cursor-pointer focus:border-[#34538F]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]" />
    </div>
  );
}

export default function McTargetsPage() {
  const [state, setState] = useState('');
  const [minBeds, setMinBeds] = useState('40');
  const [minSk, setMinSk] = useState('15');
  const [minCMA, setMinCMA] = useState('25');
  const [minOcc, setMinOcc] = useState('70');
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.mcTargets({
        state: state || undefined,
        min_beds: minBeds || undefined,
        min_skilled: minSk || undefined,
        min_county_ma: minCMA || undefined,
        min_occupancy: minOcc || undefined,
        limit: 50,
      }).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [state, minBeds, minSk, minCMA, minOcc]);

  const downloadCsv = () => {
    if (!data.items.length) return;
    const cols = ['ccn','name','state','county','city','beds','best_fit_score','mc_gap','occupancy_pct','pct_skilled_est','pct_managed_care_est','pct_medicaid','county_ma_pct','opportunity_score','confidence'];
    const head = cols.join(',');
    const rows = data.items.map((r) => cols.map((c) => {
      const v = r[c];
      if (v == null) return '';
      if (typeof v === 'string' && (v.includes(',') || v.includes('"'))) return `"${v.replace(/"/g, '""')}"`;
      return v;
    }).join(','));
    const blob = new Blob([[head, ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mc-contract-targets${state ? '-' + state : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Intelligence', to: '/managed-care' }, { label: 'MC Contract Targets' }]} />

        <header className="mt-3 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
              <Target size={11} /> Managed-care business development
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622] mt-2">MC Contract Targets</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[680px]">
              Best-fit prospects for managed-care contract growth: facilities with high estimated short-stay skilled census,
              healthy occupancy, and a wide gap between county MA penetration and their current estimated MC share.
            </p>
          </div>
          <button
            onClick={downloadCsv}
            disabled={!data.items.length}
            className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#34538F] disabled:opacity-40"
            data-testid="mc-targets-csv"
          >
            <Download size={13} /> Export CSV ({data.items.length})
          </button>
        </header>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Select testId="mct-state" value={state} onChange={setState} options={US_STATES} placeholder="State (all)" />
          <Select testId="mct-beds" value={minBeds} onChange={setMinBeds} options={[
            { value: '0', label: 'Any size' }, { value: '40', label: 'Beds ≥ 40' }, { value: '80', label: 'Beds ≥ 80' }, { value: '120', label: 'Beds ≥ 120' }]} placeholder="Min beds" />
          <Select testId="mct-skilled" value={minSk} onChange={setMinSk} options={[
            { value: '5', label: 'Skilled ≥ 5%' }, { value: '15', label: 'Skilled ≥ 15%' }, { value: '25', label: 'Skilled ≥ 25%' }, { value: '40', label: 'Skilled ≥ 40%' }]} placeholder="Min skilled" />
          <Select testId="mct-cma" value={minCMA} onChange={setMinCMA} options={[
            { value: '15', label: 'County MA ≥ 15%' }, { value: '25', label: 'County MA ≥ 25%' }, { value: '40', label: 'County MA ≥ 40%' }, { value: '55', label: 'County MA ≥ 55%' }]} placeholder="Min county MA" />
          <Select testId="mct-occ" value={minOcc} onChange={setMinOcc} options={[
            { value: '60', label: 'Occ ≥ 60%' }, { value: '70', label: 'Occ ≥ 70%' }, { value: '85', label: 'Occ ≥ 85%' }]} placeholder="Min occupancy" />
        </div>

        <div className="mb-3 flex items-center gap-2 flex-wrap text-[11.5px] text-[#5A6660]">
          <span data-testid="mct-result-count">{fmtInt(data.total)} prospects match · showing top {data.items.length}</span>
          <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E5F0EA] text-[#2D5F4F]">
            <ShieldCheck size={11} /> Public-data estimates
          </span>
        </div>

        {/* Table */}
        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Facility</th>
                  <th className="text-right px-4 py-2.5">Beds</th>
                  <th className="text-right px-4 py-2.5">Occ.</th>
                  <th className="text-right px-4 py-2.5">Skilled</th>
                  <th className="text-right px-4 py-2.5" title="Estimated current Managed Care share">MC now</th>
                  <th className="text-right px-4 py-2.5" title="County Medicare Advantage penetration">County MA%</th>
                  <th className="text-right px-4 py-2.5" title="County MA penetration minus current MC share">Gap</th>
                  <th className="text-right px-4 py-2.5">Best-fit</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#7A857F]">Loading prospects…</td></tr>}
                {!loading && data.items.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[#7A857F]">No prospects match these filters. Loosen the criteria.</td></tr>
                )}
                {!loading && data.items.map((r) => (
                  <tr key={r.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`mct-row-${r.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/facilities/${r.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{r.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">{r.city ? `${r.city}, ` : ''}{r.state} · CCN {r.ccn}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.beds)}</td>
                    <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums">{fmtPct(r.occupancy_pct)}</td>
                    <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums font-semibold">{fmtPct(r.pct_skilled_est)}</td>
                    <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtPct(r.pct_managed_care_est)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(r.county_ma_pct)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-[#E0EAF8] text-[#34538F]">
                        +{r.mc_gap?.toFixed(1)} pts
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#34538F] tabular-nums">{r.best_fit_score?.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            <strong>Scoring:</strong> {data.scoring}.{' '}
            <strong>Methodology:</strong> {data.methodology}{' '}
            <strong>Source:</strong> {data.source}.{' '}
            All values are <strong>public-data estimates</strong>. No PHI, no patient-level data, no referral data.
          </span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
